/**
 * Patrol Schedule Cache Service
 * Caches patrol schedule data to avoid repeated API calls
 * 
 * Cache Strategy:
 * - In-memory cache for fast access during session
 * - sessionStorage for persistence across page refreshes
 * - TTL-based expiration for patrol schedules
 */

class PatrolScheduleCache {
  constructor() {
    // In-memory cache: Map<'patrolSchedules', { data: Array, timestamp: number }>
    this.memoryCache = new Map();
    
    // Cache configuration
    this.config = {
      // Patrol schedules expire after 5 minutes (they can change frequently)
      PATROL_SCHEDULES_TTL: 5 * 60 * 1000, // 5 minutes in milliseconds
      // Storage key for sessionStorage
      STORAGE_KEY: 'patrolScheduleCache',
    };
    
    // Load cache from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Get cache key for patrol schedules
   */
  getCacheKey() {
    return 'patrolSchedules';
  }

  /**
   * Load cache from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.STORAGE_KEY);
      if (stored) {
        const cacheData = JSON.parse(stored);
        const now = Date.now();
        
        // Load valid entries only
        for (const [key, value] of Object.entries(cacheData)) {
          // Check if entry is still valid
          const age = now - value.timestamp;
          
          if (age < this.config.PATROL_SCHEDULES_TTL) {
            this.memoryCache.set(key, value);
          }
        }
        
        console.log(`📦 Loaded ${this.memoryCache.size} cached patrol schedule entries from storage`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load patrol schedule cache from storage:', error);
    }
  }

  /**
   * Save cache to sessionStorage
   */
  saveToStorage() {
    try {
      const cacheData = {};
      this.memoryCache.forEach((value, key) => {
        cacheData[key] = value;
      });
      
      sessionStorage.setItem(this.config.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('⚠️ Failed to save patrol schedule cache to storage:', error);
      // If storage is full, clear cache
      this.clearAll();
    }
  }

  /**
   * Check if a cache entry is valid
   */
  isValid(entry) {
    if (!entry) return false;
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    return age < this.config.PATROL_SCHEDULES_TTL;
  }

  /**
   * Get cached patrol schedules
   * @returns {Array|null} - Cached patrol schedules or null if not found/invalid
   */
  get() {
    const key = this.getCacheKey();
    const entry = this.memoryCache.get(key);
    
    if (!entry || !entry.data) return null;
    
    // Check if entry is still valid
    if (!this.isValid(entry)) {
      // Cache expired, remove it
      this.memoryCache.delete(key);
      this.saveToStorage();
      return null;
    }
    
    console.log(`✅ Cache hit for patrol schedules`);
    return entry.data;
  }

  /**
   * Set cached patrol schedules
   * @param {Array} schedules - Patrol schedules array
   */
  set(schedules) {
    if (!schedules || !Array.isArray(schedules)) return;
    
    const key = this.getCacheKey();
    
    const entry = {
      data: schedules,
      timestamp: Date.now(),
    };
    
    this.memoryCache.set(key, entry);
    this.saveToStorage();
    
    console.log(`💾 Cached ${schedules.length} patrol schedules`);
  }

  /**
   * Clear cached patrol schedules
   */
  clear() {
    const key = this.getCacheKey();
    this.memoryCache.delete(key);
    this.saveToStorage();
    console.log(`🗑️ Cleared patrol schedule cache`);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.memoryCache.clear();
    try {
      sessionStorage.removeItem(this.config.STORAGE_KEY);
    } catch (error) {
      console.warn('⚠️ Failed to clear cache from storage:', error);
    }
    console.log('🗑️ Cleared all patrol schedule cache');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const key = this.getCacheKey();
    const entry = this.memoryCache.get(key);
    
    return {
      hasCachedData: !!entry && this.isValid(entry),
      cacheSize: entry?.data?.length || 0,
      cacheAge: entry ? Date.now() - entry.timestamp : null,
      ttl: this.config.PATROL_SCHEDULES_TTL,
    };
  }
}

// Export singleton instance
export default new PatrolScheduleCache();

